import { infiniteQueryOptions, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, rawFetch, unwrap } from '~/lib/api'
import { toast } from '~/ui/toast'

// Club-wide (all teams, all seasons: DSGVO duties don't end with a season). Prefix 'gdpr' is what
// the server invalidates after consent, document and erasure changes.
export const gdprKeys = {
  all: ['gdpr'] as const,
  consents: ['gdpr', 'consents'] as const,
  documents: (personId?: string) => ['gdpr', 'documents', personId ?? 'all'] as const,
  audit: (f: AuditFilter) => ['gdpr', 'audit', f.entityType ?? '', f.actor ?? ''] as const,
}

export type AuditFilter = { entityType?: string; actor?: string }

export const consentsQuery = queryOptions({
  queryKey: gdprKeys.consents,
  queryFn: () => unwrap(api.gdpr.consents.$get()),
})

export const documentsQuery = (personId?: string) =>
  queryOptions({
    queryKey: gdprKeys.documents(personId),
    queryFn: () => unwrap(api.gdpr.documents.$get({ query: personId ? { personId } : {} })),
  })

export const auditQuery = (f: AuditFilter) =>
  infiniteQueryOptions({
    queryKey: gdprKeys.audit(f),
    queryFn: ({ pageParam }) =>
      unwrap(
        api.gdpr.audit.$get({
          query: {
            limit: '40',
            ...(pageParam ? { cursor: String(pageParam) } : {}),
            ...(f.entityType ? { entityType: f.entityType } : {}),
            ...(f.actor ? { actor: f.actor } : {}),
          },
        }),
      ),
    initialPageParam: 0,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  })

export type ConsentsData = Awaited<ReturnType<NonNullable<typeof consentsQuery.queryFn>>>
export type PersonRow = ConsentsData['people'][number]
export type Purpose = ConsentsData['purposes'][number]
export type DocumentRow = Awaited<ReturnType<NonNullable<ReturnType<typeof documentsQuery>['queryFn']>>>[number]
export type AuditPage = Awaited<ReturnType<NonNullable<ReturnType<typeof auditQuery>['queryFn']>>>
export type AuditItem = AuditPage['items'][number]

/** Toggle one consent; the matrix updates at once and rolls back if the server refuses. */
export function useSetConsent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: { personId: string; purpose: Purpose; state: 'granted' | 'revoked' }) => unwrap(api.gdpr.consents.$post({ json: v })),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: gdprKeys.consents })
      const prev = qc.getQueryData<ConsentsData>(gdprKeys.consents)
      if (prev)
        qc.setQueryData<ConsentsData>(gdprKeys.consents, {
          ...prev,
          people: prev.people.map((p) =>
            p.id === v.personId
              ? { ...p, consents: { ...p.consents, [v.purpose]: { state: v.state, changedAt: new Date().toISOString(), bySelf: false } } }
              : p,
          ),
        })
      return { prev }
    },
    onError: (e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(gdprKeys.consents, ctx.prev)
      toast.error(e instanceof ApiError ? e.message : 'Einwilligung konnte nicht gespeichert werden.')
    },
    onSettled: () => qc.invalidateQueries({ queryKey: gdprKeys.consents }),
  })
}

/** Download the Art. 15 export as a file (the browser keeps the server's filename). */
export async function downloadExport(personId: string) {
  const res = await rawFetch(`/api/gdpr/people/${personId}/export`)
  const cd = res.headers.get('content-disposition') ?? ''
  const name = decodeURIComponent(/filename\*=UTF-8''([^;]+)/.exec(cd)?.[1] ?? /filename="([^"]+)"/.exec(cd)?.[1] ?? 'dsgvo-export.json')
  const url = URL.createObjectURL(await res.blob())
  const a = document.createElement('a')
  a.href = url
  a.download = name
  a.click()
  // Revoke after the click has started the download (v1 leaked blob URLs, W17).
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
